/**
 * Screenshot Component
 *
 * Компонент для отображения скриншотов с возможностью увеличения по клику.
 * Автоматически выбирает мобильную или десктопную версию изображения.
 *
 * @param description - Описание изображения для alt текста и подписи
 * @param imageName - Базовое имя файла (без суффикса _mobile/_desktop и расширения)
 *
 * Пример использования:
 * <Screenshot
 *   description="Главный экран с областью загрузки"
 *   imageName="screen_01"
 * />
 *
 * Ожидаемая структура файлов в /public/guide/:
 * - screen_01_mobile.png
 * - screen_01_desctop.png (обратите внимание на орфографию "desctop")
 */

import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { X } from "lucide-react";

interface ScreenshotProps {
  description: string;
  imageName: string;
}

const Screenshot = ({ description, imageName }: ScreenshotProps) => {
  const isMobile = useIsMobile();
  const imagePath = isMobile ? `/user-guide/${imageName}_mobile.png` : `/user-guide/${imageName}_desctop.png`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="my-6 cursor-pointer group flex flex-col items-center">
          <img
            src={imagePath}
            alt={description}
            className={`
              rounded-lg border shadow-sm group-hover:shadow-md transition-shadow
              ${
                isMobile
                  ? "w-3/4 max-w-xs" // На мобильных: 75% ширины, но не больше 320px
                  : "w-auto max-w-2xl max-h-96" // На десктопе: авто ширина, макс 672px по ширине или 384px по высоте
              }
              object-contain
            `}
          />
          <p className="text-sm text-muted-foreground mt-2 italic text-center group-hover:text-foreground transition-colors max-w-md">
            {description} (кликните для увеличения)
          </p>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 [&>button]:hidden">
        <div className="relative">
          <img src={imagePath} alt={description} className="w-full h-auto max-h-[95vh] object-contain" />
          <DialogClose asChild>
            <button
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-primary-foreground transition-colors z-10"
              aria-label="Закрыть"
            >
              <X size={20} />
            </button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Screenshot;
